import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocale } from '../../context';
import { isAmountReady, useJackpotHitFlash } from '../home/RollingAmount';
import {
  ensureRealtimeConnected,
  isRealtimeConnected,
  subscribeRealtimeStatus,
  subscribeRealtimeTopic,
} from '../../utils/realtimeSocket';
import {
  RELUM_CARDS,
  RELUM_HOST_EXTRAS,
  RELUM_PRIMARY_BYN_TOTAL,
  RELUM_PRIMARY_ID,
} from './relumCards';
import { RELUM_THUMB_HINTS } from './relumThumbHints';
import '../../styles/relum-jackpot-widget.css';
import '../../styles/relum-cards-theme.css';

const POLL_MS = 8000;
const POLL_WS_MS = 60000;

/** Map Relum Amusnet (104_1) tier order → our /api/casino/jackpots keys */
const PRIMARY_LEVEL_KEYS = ['spades', 'hearts', 'diamonds', 'clubs'];

/** pm.by Relum-like visual creep (TRY/sec) — slower than live pool rates */
const RELUM_VISUAL_RATE = {
  spades: 0.38,
  hearts: 0.16,
  diamonds: 0.045,
  clubs: 0.012,
};

const RELUM_LAG_SEC = 10;
const RELUM_RATE_CAP = 0.55;

const PROVIDER_BY_SLUG = {
  amusnet: 'Amusnet',
  pateplay: 'Pateplay',
  pragmaticplay: 'Pragmatic Play',
  egtdigital: 'EGT Digital',
  egyptquest: 'Egypt Quest',
  '7777gaming': '7777 gaming',
  endorphina: 'Endorphina',
};

function hintFromGameSrc(src) {
  const file = decodeURIComponent(String(src || '').split('/').pop() || '');
  const uuidMatch = file.match(/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i);
  if (uuidMatch) {
    const mapped = RELUM_THUMB_HINTS[uuidMatch[1].toLowerCase()];
    if (mapped) return mapped;
  }
  let hint = file
    .replace(/\.(webp|png|jpe?g|gif)(\?.*)?$/i, '')
    .replace(/^[0-9]+-/i, '')
    .replace(/^[0-9a-f]{8}-[0-9a-f-]{27}_?/i, '')
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!hint || /^[0-9a-f-]{30,}$/i.test(hint)) return '';
  return hint;
}

const thumbGameIdCache = new Map();

function scoreResolvedGame(game, qLc, provider) {
  const name = String(game?.name || '').toLowerCase();
  const vendor = String(game?.vendor || game?.provider || '').toLowerCase();
  let score = 0;
  if (name === qLc) score += 100;
  else if (name.startsWith(qLc)) score += 70;
  else if (name.includes(qLc)) score += 40;
  if (provider) {
    const p = provider.toLowerCase();
    if (vendor === p.toLowerCase() || vendor.includes(p.slice(0, 6))) score += 25;
  }
  // Prefer non-Drakon (slots launch path)
  if (String(game?.aggregator || '').toLowerCase() === 'drakon') score -= 50;
  if (game?.icon_url && !String(game.icon_url).includes('_placeholder')) score += 5;
  return score;
}

async function resolveGameIdFromThumb(src, slug) {
  const cacheKey = `${slug || ''}|${src || ''}`;
  if (thumbGameIdCache.has(cacheKey)) return thumbGameIdCache.get(cacheKey);

  const hint = hintFromGameSrc(src);
  if (!hint || hint.length < 2) {
    thumbGameIdCache.set(cacheKey, null);
    return null;
  }
  const provider = PROVIDER_BY_SLUG[slug] || '';
  const variants = [];
  const push = (q) => {
    const s = String(q || '').replace(/\s+/g, ' ').trim();
    if (s.length >= 2 && !variants.includes(s)) variants.push(s);
  };
  push(hint);
  push(hint.replace(/\s+\d+$/g, ''));
  const words = hint.split(' ').filter(Boolean);
  if (words.length > 2) push(words.slice(0, 3).join(' '));
  if (words.length > 1) push(words.slice(0, 2).join(' '));

  let best = null;
  let bestScore = -1;

  for (const q of variants) {
    const tries = [];
    const base = `/api/casino/games?lobby=slots&sort=popular&limit=12&q=${encodeURIComponent(q)}`;
    if (provider) {
      tries.push(`${base}&provider=${encodeURIComponent(provider)}`);
    }
    tries.push(base);
    // live jackpot thumbs (rare)
    tries.push(`/api/casino/games?lobby=live&sort=popular&limit=8&q=${encodeURIComponent(q)}`);
    for (const url of tries) {
      try {
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) continue;
        const json = await res.json();
        const games = Array.isArray(json?.games) ? json.games : [];
        if (!games.length) continue;
        const qLc = q.toLowerCase();
        for (const g of games) {
          const score = scoreResolvedGame(g, qLc, provider);
          if (score > bestScore && g?.id != null) {
            bestScore = score;
            best = String(g.id);
          }
        }
        if (bestScore >= 100) {
          thumbGameIdCache.set(cacheKey, best);
          return best;
        }
      } catch {
        /* ignore */
      }
    }
  }
  thumbGameIdCache.set(cacheKey, best);
  return best;
}

function buildHostStyle() {
  const style = {
    display: 'block',
    width: '100%',
  };
  for (const [k, v] of Object.entries(RELUM_HOST_EXTRAS)) {
    style[`--${k}`] = v;
  }
  for (const card of RELUM_CARDS) {
    for (const [k, v] of Object.entries(card.vars || {})) {
      style[`--${card.id}-${k}`] = v;
    }
  }
  return style;
}

const HOST_STYLE = buildHostStyle();

function clamp(n, lo, hi) {
  return Math.min(hi, Math.max(lo, n));
}

function round2(n) {
  return Math.round(n * 100) / 100;
}

/**
 * Relum/pm.by style: start a few seconds behind, creep slowly, catch up gently.
 * Display is plain formatted text (no digit-ribbon) — matches live pm.by Relum widget.
 */
function useRelumLaggedAmount(target, ratePerSec = 0.2) {
  const visualRate = clamp(Number(ratePerSec) || 0.2, 0.006, RELUM_RATE_CAP);
  const [display, setDisplay] = useState(null);
  const curRef = useRef(null);
  const targetRef = useRef(target);
  const lastTsRef = useRef(0);

  useEffect(() => {
    targetRef.current = target;
  }, [target]);

  useEffect(() => {
    let raf = 0;
    lastTsRef.current = performance.now();

    const frame = (now) => {
      const t = Number(targetRef.current);
      if (!Number.isFinite(t)) {
        raf = window.requestAnimationFrame(frame);
        return;
      }

      const dt = Math.min(0.05, Math.max(0, (now - lastTsRef.current) / 1000));
      lastTsRef.current = now;

      let cur = curRef.current;
      if (cur == null || Number.isNaN(cur)) {
        cur = round2(Math.max(0, t - visualRate * RELUM_LAG_SEC));
        curRef.current = cur;
        setDisplay(cur);
        raf = window.requestAnimationFrame(frame);
        return;
      }

      const gap = t - cur;
      let step;
      if (gap < -Math.max(25, t * 0.12)) {
        // Jackpot hit / reset — ease toward new pool
        step = gap * Math.min(1, 0.12 * (dt / 0.016));
      } else if (gap > visualRate * 90) {
        step = visualRate * 1.65 * dt;
      } else if (gap > 0) {
        step = Math.min(gap, visualRate * 1.08 * dt);
      } else {
        // Soft idle creep so the counter never looks frozen
        step = visualRate * 0.35 * dt;
      }

      cur = round2(cur + step);
      if (cur !== curRef.current) {
        curRef.current = cur;
        setDisplay(cur);
      }
      raf = window.requestAnimationFrame(frame);
    };

    raf = window.requestAnimationFrame(frame);
    return () => window.cancelAnimationFrame(raf);
  }, [visualRate]);

  return display;
}

function formatRelumAmount(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  // pm.by Relum uses en-US grouping (792,811.57) even on /ru — keep locale-aware for TR.
  return n.toLocaleString(locale === 'tr' ? 'tr-TR' : 'en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function LiveRoll({ value, locale, className, ratePerSec = 0.2 }) {
  const display = useRelumLaggedAmount(value == null ? NaN : Number(value), ratePerSec);
  if (!isAmountReady(display)) {
    return <div className={className}>—</div>;
  }
  // Plain text tick like pm.by Relum (no per-digit odometer ribbon during creep).
  return <div className={className}>{formatRelumAmount(display, locale)}</div>;
}

function JackpotCard({ card, levels, total, totalRate, currency, locale, onNavigate }) {
  const openCategory = () => {
    const provider = PROVIDER_BY_SLUG[card.slug];
    if (provider) {
      onNavigate?.('casino', { casinoProvider: provider });
      return;
    }
    onNavigate?.('casino');
  };

  const openGame = async (src) => {
    try {
      const id = await resolveGameIdFromThumb(src, card.slug);
      if (!id) {
        openCategory();
        return;
      }
      onNavigate?.('casino', { casinoGameId: String(id), casinoGameDemo: false });
    } catch {
      openCategory();
    }
  };

  return (
    <div
      id={`jackpot-item-${card.id}`}
      className={`jackpot-item ${card.classes || 'logo-half-overlay cursor-pointer'}`.trim()}
      role="link"
      tabIndex={0}
      aria-label={PROVIDER_BY_SLUG[card.slug] || card.slug || 'Jackpot'}
      onClick={(e) => {
        if (e.target.closest('button, a, .game, .game-tab-item')) return;
        openCategory();
      }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        if (e.target !== e.currentTarget) return;
        e.preventDefault();
        openCategory();
      }}
    >
      <div className="jackpot_container">
        <div className="container">
          <div className="wrapper">
            <div className="left">
              <img src={card.logo} className="logo" alt="" draggable={false} />
              <div className="left-info-wrapper">
                <div className="total-amount">
                  <div className="amount-wrapper">
                    <LiveRoll
                      value={total}
                      ratePerSec={totalRate}
                      locale={locale}
                      className="amount level-amount-sum"
                    />
                    <div className="currency">{currency}</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="right">
              <div className={`widget-levels${levels.length === 3 ? ' first-full-width' : ''}`}>
                {levels.map((lvl, index) => (
                  <div key={`${card.id}-${lvl.label}-${index}`} className="widget-level cursor-pointer" data-index={index}>
                    <div className="widget-level-icon">
                      <img src={lvl.icon} alt="" draggable={false} />
                    </div>
                    <div className="widget-level-data-wrapper">
                      <div className="widget-level-name">
                        <span>{lvl.label}</span>
                      </div>
                      <div className="widget-level-amount-wrapper">
                        <LiveRoll
                          value={lvl.current}
                          ratePerSec={lvl.ratePerSec}
                          locale={locale}
                          className={`widget-level-currency level-amount-Level_${index + 1}`}
                        />
                        <div className="widget-level-amount">{currency}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="games-wrapper">
            <div className="swiper-container">
              <div className="games">
                {(card.games || []).map((src, index) => (
                  <button
                    key={`${card.id}-g-${index}`}
                    type="button"
                    className="game-tab-item game-horizontal game"
                    data-index={index}
                    aria-label={hintFromGameSrc(src) || 'Game'}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openGame(src);
                    }}
                  >
                    <img src={src} loading="lazy" alt="" draggable={false} />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Digitech Relum &lt;jackpot-widget&gt; — full carousel from pm.by shadow dump.
 * Primary Amusnet (104_1) uses live TRY /api/casino/jackpots; sibling cards scale Relum BYN seeds to TRY.
 */
export default function DigitechRelumJackpot({ onNavigate, hideHeader = false, autoPlayMs = 0 } = {}) {
  const { t: tr, locale } = useLocale();
  const [data, setData] = useState(null);
  const listRef = useRef(null);
  const indexRef = useRef(0);
  const pausedRef = useRef(false);
  const draggingRef = useRef(false);
  const programmaticRef = useRef(false);
  const resumeTimerRef = useRef(0);
  const progClearTimerRef = useRef(0);
  const flash = useJackpotHitFlash(data);

  const userIdleMs = Math.max(6000, Number(autoPlayMs) || 0);

  useEffect(() => {
    let alive = true;
    let pollTimer = 0;

    const load = async () => {
      try {
        const res = await fetch('/api/casino/jackpots', { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        if (alive) setData(json);
      } catch {
        /* ignore */
      }
    };

    const armPoll = () => {
      window.clearInterval(pollTimer);
      const ms = isRealtimeConnected() ? POLL_WS_MS : POLL_MS;
      pollTimer = window.setInterval(() => {
        if (!isRealtimeConnected()) load();
      }, ms);
    };

    ensureRealtimeConnected();
    const unsub = subscribeRealtimeTopic('jackpots', (json) => {
      if (alive && json) setData(json);
    });
    const unsubStatus = subscribeRealtimeStatus(() => {
      armPoll();
      if (!isRealtimeConnected() && alive) load();
    });
    load();
    armPoll();

    return () => {
      alive = false;
      unsub();
      unsubStatus();
      window.clearInterval(pollTimer);
    };
  }, []);

  const currency =
    (data?.currencySymbol === 'TL' ? 'TRY' : data?.currencySymbol) ||
    (data?.currency === 'TL' ? 'TRY' : data?.currency) ||
    'TRY';

  const primaryLevels = useMemo(() => {
    return PRIMARY_LEVEL_KEYS.map((key, index) => {
      const exploding = flash?.tier === key;
      const row = data?.[key] || {};
      const seed = RELUM_CARDS[0]?.levels?.[index];
      const ratePerSec = RELUM_VISUAL_RATE[key] ?? 0.05;
      return {
        key,
        label: seed?.label || ['MEGA', 'MAJOR', 'MINOR', 'MINI'][index],
        icon: seed?.icon || '',
        current: exploding ? flash.amount : (row.current ?? null),
        ratePerSec,
      };
    });
  }, [data, flash]);

  const primaryTotal = useMemo(() => {
    const sum = primaryLevels.reduce((acc, lvl) => acc + (Number(lvl.current) || 0), 0);
    return sum > 0 ? sum : null;
  }, [primaryLevels]);

  const primaryTotalRate = useMemo(
    () => primaryLevels.reduce((acc, lvl) => acc + (Number(lvl.ratePerSec) || 0), 0),
    [primaryLevels],
  );

  // Sibling cards: Relum BYN seeds × live TRY/Amusnet ratio (LiveRoll lags the amounts)
  const tryScale = useMemo(() => {
    const byn = RELUM_PRIMARY_BYN_TOTAL || 1;
    const tryTotal = Number(primaryTotal);
    if (!Number.isFinite(tryTotal) || tryTotal <= 0) return 1;
    return tryTotal / byn;
  }, [primaryTotal]);

  const cardsView = useMemo(() => {
    return RELUM_CARDS.map((card) => {
      if (card.id === RELUM_PRIMARY_ID) {
        return {
          card,
          levels: primaryLevels.map((lvl, index) => ({
            ...lvl,
            icon: card.levels[index]?.icon || lvl.icon,
            label: card.levels[index]?.label || lvl.label,
          })),
          total: primaryTotal,
          totalRate: primaryTotalRate,
        };
      }
      const levels = (card.levels || []).map((lvl, index) => {
        const primaryRate = RELUM_VISUAL_RATE[PRIMARY_LEVEL_KEYS[Math.min(index, PRIMARY_LEVEL_KEYS.length - 1)]] || 0.05;
        const weight = (Number(lvl.amount) || 0) / (RELUM_PRIMARY_BYN_TOTAL || 1);
        return {
          label: lvl.label,
          icon: lvl.icon,
          current: Number(lvl.amount) * tryScale,
          ratePerSec: clamp(primaryRate * Math.max(0.15, weight * 3.5), 0.006, RELUM_RATE_CAP * 0.85),
        };
      });
      const total = levels.reduce((acc, lvl) => acc + (Number(lvl.current) || 0), 0);
      const totalRate = levels.reduce((acc, lvl) => acc + (Number(lvl.ratePerSec) || 0), 0);
      return { card, levels, total, totalRate };
    });
  }, [primaryLevels, primaryTotal, primaryTotalRate, tryScale]);

  const titleRaw = tr('home.jackpot.collection');
  const title =
    !titleRaw || titleRaw === 'home.jackpot.collection' || String(titleRaw).startsWith('home.')
      ? locale === 'tr'
        ? 'Jackpot Koleksiyonu'
        : 'Jackpot Collection'
      : titleRaw;

  const getCards = () => {
    const el = listRef.current;
    if (!el) return [];
    return [...el.querySelectorAll('.jackpot-item')];
  };

  const isTouchUi = () => {
    if (typeof window === 'undefined') return false;
    try {
      return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
    } catch {
      return navigator.maxTouchPoints > 0;
    }
  };

  const touchUiRef = useRef(false);
  useEffect(() => {
    touchUiRef.current = isTouchUi();
  }, []);

  const cancelProgrammaticScroll = ({ freeze = false } = {}) => {
    programmaticRef.current = false;
    window.clearTimeout(progClearTimerRef.current);
    // Only freeze mid-smooth-scroll when explicitly requested (e.g. touchstart
    // interrupting autoplay). Never call scrollTo during native scroll events —
    // that kills iOS/Android momentum and causes the swipe bug.
    if (!freeze) return;
    const el = listRef.current;
    if (!el) return;
    const x = el.scrollLeft;
    el.scrollTo({ left: x, behavior: 'auto' });
  };

  const goTo = (i, { smooth = true } = {}) => {
    const el = listRef.current;
    const cards = getCards();
    if (!el || !cards.length) return;
    if (draggingRef.current) return;
    const n = cards.length;
    const next = ((i % n) + n) % n;
    indexRef.current = next;
    programmaticRef.current = true;
    window.clearTimeout(progClearTimerRef.current);
    const useSmooth = smooth && !touchUiRef.current;
    const left = cards[next].offsetLeft;
    el.scrollTo({ left, behavior: useSmooth ? 'smooth' : 'auto' });
    progClearTimerRef.current = window.setTimeout(() => {
      programmaticRef.current = false;
      syncIndexFromScroll();
    }, useSmooth ? 520 : 40);
  };

  const syncIndexFromScroll = () => {
    const el = listRef.current;
    const cards = getCards();
    if (!el || !cards.length) return;
    const step = Math.max(1, cards[0].offsetWidth || el.clientWidth);
    indexRef.current = Math.max(0, Math.min(cards.length - 1, Math.round(el.scrollLeft / step)));
  };

  const pauseAuto = () => {
    pausedRef.current = true;
    window.clearTimeout(resumeTimerRef.current);
  };

  const scheduleResume = (delay = userIdleMs) => {
    if (!(Number(autoPlayMs) > 0)) return;
    window.clearTimeout(resumeTimerRef.current);
    resumeTimerRef.current = window.setTimeout(() => {
      if (!draggingRef.current) pausedRef.current = false;
    }, delay);
  };

  const noteUserInteraction = ({ freeze = false } = {}) => {
    cancelProgrammaticScroll({ freeze });
    pauseAuto();
    scheduleResume(userIdleMs);
  };

  const scrollBy = (dir) => {
    noteUserInteraction({ freeze: true });
    syncIndexFromScroll();
    const wasPaused = pausedRef.current;
    pausedRef.current = false;
    goTo(indexRef.current + dir, { smooth: !touchUiRef.current });
    pausedRef.current = wasPaused;
    scheduleResume(userIdleMs);
  };

  // Auto-advance — pause briefly after user swipe, then resume like desktop.
  useEffect(() => {
    const ms = Number(autoPlayMs) || 0;
    if (ms <= 0) return undefined;
    const id = window.setInterval(() => {
      if (pausedRef.current || draggingRef.current || programmaticRef.current || document.hidden) return;
      syncIndexFromScroll();
      goTo(indexRef.current + 1);
    }, ms);
    return () => {
      window.clearInterval(id);
      window.clearTimeout(resumeTimerRef.current);
      window.clearTimeout(progClearTimerRef.current);
    };
  }, [autoPlayMs, userIdleMs]);

  // Native scroll — pause autoplay only; never freeze scrollLeft mid-gesture.
  useEffect(() => {
    if (data?.enabled === false) return undefined;
    const el = listRef.current;
    if (!el) return undefined;
    let settleTimer = 0;
    const onScroll = () => {
      if (programmaticRef.current) return;
      pauseAuto();
      scheduleResume(userIdleMs);
      window.clearTimeout(settleTimer);
      settleTimer = window.setTimeout(() => {
        syncIndexFromScroll();
      }, 140);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.clearTimeout(settleTimer);
    };
  }, [cardsView.length, data?.enabled, userIdleMs, autoPlayMs]);

  // Touch: native overflow + CSS scroll-snap only.
  // Mouse: optional grab-drag for desktop.
  useEffect(() => {
    if (data?.enabled === false) return undefined;
    const el = listRef.current;
    if (!el) return undefined;

    const onTouchStart = () => {
      draggingRef.current = true;
      // Interrupt in-flight autoplay smooth scroll once, then leave native swipe alone.
      if (programmaticRef.current) cancelProgrammaticScroll({ freeze: true });
      pauseAuto();
    };
    const onTouchEnd = () => {
      draggingRef.current = false;
      window.setTimeout(() => syncIndexFromScroll(), 80);
      scheduleResume(userIdleMs);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchend', onTouchEnd, { passive: true });
    el.addEventListener('touchcancel', onTouchEnd, { passive: true });

    if (isTouchUi()) {
      return () => {
        el.removeEventListener('touchstart', onTouchStart);
        el.removeEventListener('touchend', onTouchEnd);
        el.removeEventListener('touchcancel', onTouchEnd);
      };
    }

    let startX = 0;
    let startScroll = 0;
    let moved = false;
    let active = false;

    const onDown = (e) => {
      if (e.pointerType === 'touch') return;
      if (e.button != null && e.button !== 0) return;
      if (e.target && e.target.closest && e.target.closest('button, a, .game, .game-tab-item, input, textarea')) {
        return;
      }
      active = true;
      moved = false;
      draggingRef.current = false;
      startX = e.clientX;
      startScroll = el.scrollLeft;
      noteUserInteraction({ freeze: true });
      el.classList.add('is-dragging');
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
    };

    const onMove = (e) => {
      if (!active || e.pointerType === 'touch') return;
      const dx = e.clientX - startX;
      if (!moved && Math.abs(dx) < 6) return;
      moved = true;
      draggingRef.current = true;
      programmaticRef.current = false;
      el.scrollLeft = startScroll - dx;
    };

    const onUp = (e) => {
      if (!active) return;
      active = false;
      el.classList.remove('is-dragging');
      try {
        el.releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }
      if (moved) {
        const cards = getCards();
        const step = Math.max(1, cards[0]?.offsetWidth || el.clientWidth);
        const dx = e.clientX - startX;
        let next = Math.round(el.scrollLeft / step);
        if (Math.abs(dx) > Math.min(72, step * 0.18)) {
          next = dx < 0 ? Math.floor(startScroll / step) + 1 : Math.ceil(startScroll / step) - 1;
        }
        goTo(next);
      } else {
        syncIndexFromScroll();
      }
      draggingRef.current = false;
      scheduleResume(userIdleMs);
    };

    const onClickCapture = (e) => {
      if (e.target && e.target.closest && e.target.closest('button, a, .game, .game-tab-item')) {
        moved = false;
        return;
      }
      if (!moved) return;
      e.preventDefault();
      e.stopPropagation();
      moved = false;
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerup', onUp);
    el.addEventListener('pointercancel', onUp);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchend', onTouchEnd);
      el.removeEventListener('touchcancel', onTouchEnd);
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerup', onUp);
      el.removeEventListener('pointercancel', onUp);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, [cardsView.length, data?.enabled, userIdleMs, autoPlayMs]);

  if (data?.enabled === false) return null;

  return (
    <div
      className={`lh-relum-jackpot-widget${hideHeader ? ' is-headerless' : ''}${autoPlayMs > 0 ? ' is-autoplay' : ''}`}
      onPointerEnter={
        autoPlayMs > 0 && !touchUiRef.current
          ? () => pauseAuto()
          : undefined
      }
      onPointerLeave={
        autoPlayMs > 0 && !touchUiRef.current
          ? () => {
              if (!draggingRef.current) scheduleResume(Math.min(1200, userIdleMs));
            }
          : undefined
      }
    >
      <div id="jkpt_151" className="lh--relum-jackpot">
        <jackpot-widget className="relum-jkpt-host" style={HOST_STYLE}>
          <div className="widget-root">
            <div className="multi-wrapper">
              {!hideHeader ? (
                <div className="header">
                  <div className="header-left">
                    <div className="header-left-icon">
                      <img src="/static/content/Casino/icons/relum-header-icon.webp" alt="" />
                    </div>
                    <div className="header-left-title">{title}</div>
                  </div>
                  <div className="header-right">
                    <div className="header-right-btns">
                      <button type="button" className="all-arrow-btn all-arrow-prev-btn" onClick={() => scrollBy(-1)} aria-label="Prev">
                        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="36" height="36" rx="18" transform="matrix(-1 0 0 1 36 0)" fill="var(--setting-background-color)" />
                          <path d="M21.6992 22.6L17.0992 18L21.6992 13.4L20.2992 12L14.2992 18L20.2992 24L21.6992 22.6Z" fill="white" />
                        </svg>
                      </button>
                      <button type="button" className="all-arrow-btn all-arrow-next-btn" onClick={() => scrollBy(1)} aria-label="Next">
                        <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect width="36" height="36" rx="18" fill="var(--setting-background-color)" />
                          <path d="M14.3008 22.6L18.9008 18L14.3008 13.4L15.7008 12L21.7008 18L15.7008 24L14.3008 22.6Z" fill="white" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="jackpots-list-container">
                <div className="jackpots-list-wrapper" ref={listRef}>
                  {cardsView.map(({ card, levels, total, totalRate }) => (
                    <JackpotCard
                      key={card.id}
                      card={card}
                      levels={levels}
                      total={total}
                      totalRate={totalRate}
                      currency={currency}
                      locale={locale}
                      onNavigate={onNavigate}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </jackpot-widget>
      </div>
    </div>
  );
}
